import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2, User } from 'lucide-react';
import { useAdminUser } from '@/hooks/useAdminUser';
import { useAssignProfile } from '@/hooks/useAssignProfile';
import { useActiveExpertProfiles } from '@/hooks/useExpertProfiles';

export default function AdminUserDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: userData, isLoading, error } = useAdminUser(id!);
  const { data: activeExperts } = useActiveExpertProfiles();
  const assignProfile = useAssignProfile();

  const [selectedProfileId, setSelectedProfileId] = useState<string>('');

  const adminUser = userData?.data;

  // Sync selected profile when user data loads
  useEffect(() => {
    if (adminUser?.expertProfileId) {
      setSelectedProfileId(adminUser.expertProfileId.toString());
    } else {
      setSelectedProfileId('');
    }
  }, [adminUser?.expertProfileId]);

  useEffect(() => {
    if (adminUser) {
      document.title = `${adminUser.name} | Admin Dashboard`;
    }
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, [adminUser]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-pine-pine" />
      </div>
    );
  }

  if (error || !adminUser) {
    return (
      <div className="admin-card p-6">
        <p className="text-pine-danger">Admin user not found</p>
        <button
          onClick={() => navigate('/admin/users')}
          className="mt-4 text-pine-pine hover:text-pine-pine-deep"
        >
          Back to Users
        </button>
      </div>
    );
  }

  const roleBadgeClass =
    adminUser.role === 'super_admin'
      ? 'bg-purple-50 text-purple-700 border-purple-200'
      : 'bg-sky-50 text-sky-700 border-sky-200';

  const roleLabel = adminUser.role === 'super_admin' ? 'Super Admin' : 'Creator';

  const handleAssignProfile = () => {
    const profileId = selectedProfileId ? parseInt(selectedProfileId, 10) : null;
    assignProfile.mutate({ userId: id!, expertProfileId: profileId });
  };

  const hasProfileChanged =
    (selectedProfileId === '' && adminUser.expertProfileId !== null) ||
    (selectedProfileId !== '' && parseInt(selectedProfileId, 10) !== adminUser.expertProfileId);

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Back */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center gap-2 text-pine-ink-soft hover:text-pine-ink"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Users
        </button>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* User Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="admin-card p-6"
          >
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-pine-l1 flex items-center justify-center border-2 border-pine-line">
                <User className="w-10 h-10 text-pine-pine" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-pine-ink">{adminUser.name}</h1>
                <p className="text-pine-ink-soft mt-1">{adminUser.email}</p>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border mt-2 ${roleBadgeClass}`}
                >
                  {roleLabel}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Assign Profile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="admin-card p-6 space-y-4"
          >
            <h2 className="text-lg font-semibold text-pine-ink">Expert Profile Assignment</h2>

            {/* Currently assigned profile */}
            {adminUser.expertProfile && (
              <div className="flex items-center gap-4 p-4 rounded-lg bg-pine-hover-item border border-pine-line">
                {adminUser.expertProfile.avatarUrl ? (
                  <img
                    src={adminUser.expertProfile.avatarUrl}
                    alt={adminUser.expertProfile.name}
                    className="w-12 h-12 rounded-full object-cover border border-pine-line"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-pine-l1 flex items-center justify-center border border-pine-line">
                    <User className="w-6 h-6 text-pine-pine" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-pine-ink">{adminUser.expertProfile.name}</p>
                  <p className="text-sm text-pine-ink-soft">{adminUser.expertProfile.subtitle}</p>
                </div>
              </div>
            )}

            {/* Profile dropdown */}
            <div className="space-y-2">
              <label className="block text-sm text-pine-ink-soft">
                {adminUser.expertProfile ? 'Change assigned profile' : 'Assign an expert profile'}
              </label>
              <div className="flex items-center gap-3">
                <select
                  value={selectedProfileId}
                  onChange={(e) => setSelectedProfileId(e.target.value)}
                  className="flex-1 px-4 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink focus:border-pine-pine focus:outline-none"
                >
                  <option value="">No profile assigned</option>
                  {activeExperts?.map((expert) => (
                    <option key={expert.id} value={expert.id}>
                      {expert.name} — {expert.subtitle}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAssignProfile}
                  disabled={!hasProfileChanged || assignProfile.isPending}
                  className="admin-button-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assignProfile.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right column - Metadata */}
        <div className="space-y-6">
          <div className="admin-card p-6 space-y-4">
            <h2 className="text-lg font-semibold text-pine-ink">Details</h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-pine-ink-soft">Role</span>
                <p className="text-pine-ink">{roleLabel}</p>
              </div>
              <div>
                <span className="text-sm text-pine-ink-soft">Created</span>
                <p className="text-pine-ink">
                  {new Date(adminUser.createdAt).toLocaleDateString()}
                </p>
              </div>
              {adminUser.updatedAt && (
                <div>
                  <span className="text-sm text-pine-ink-soft">Updated</span>
                  <p className="text-pine-ink">
                    {new Date(adminUser.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
